import { useEffect, useRef, useState } from "react";
import {
  motion,
  useReducedMotion,
  useMotionValue,
  useSpring,
  useInView,
  animate,
} from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 28, className = "", once = true }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: reduce ? 0 : y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "-80px" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
};

export const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-3 font-mono text-[11px] tracking-[0.32em] uppercase text-emerald-600/90">
    <span className="h-px w-8 bg-emerald-500/60" />
    {children}
  </span>
);

export const Magnetic = ({ children, className = "" }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 18 });
  const sy = useSpring(y, { stiffness: 200, damping: 18 });

  const onMove = (e) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - r.left - r.width / 2) * 0.18);
    y.set((e.clientY - r.top - r.height / 2) * 0.3);
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      style={{ x: sx, y: sy }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={`inline-block ${className}`}
    >
      {children}
    </motion.div>
  );
};

export const CTAButton = ({
  children,
  onClick,
  variant = "primary",
  testId,
  type = "button",
  className = "",
  disabled = false,
}) => {
  const styles =
    variant === "primary"
      ? "bg-emerald-400 text-[#04120c] hover:bg-emerald-300 shadow-[0_0_0_rgba(16,185,129,0)] hover:shadow-[0_8px_40px_rgba(16,185,129,0.35)]"
      : "border border-[#10202E]/20 text-[#1C3347] hover:border-emerald-600/60 hover:text-emerald-700 bg-white/60 backdrop-blur-sm";
  return (
    <Magnetic>
      <button
        data-testid={testId}
        type={type}
        onClick={onClick}
        disabled={disabled}
        className={`group inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-sm font-semibold tracking-wide transition-[background-color,border-color,color,box-shadow] duration-300 disabled:opacity-60 ${styles} ${className}`}
      >
        <span>{children}</span>
        <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </button>
    </Magnetic>
  );
};

export const Counter = ({ to, suffix = "", className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const reduce = useReducedMotion();
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;
    if (reduce) {
      setVal(to);
      return undefined;
    }
    const controls = animate(0, to, {
      duration: 1.8,
      ease: EASE,
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to, reduce]);

  return (
    <span ref={ref} className={className}>
      {val}
      {suffix}
    </span>
  );
};
